import React, {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  FileText,
  Upload,
  Users,
  Eye,
  Download,
  Loader2,
  FileSignature,
  CheckCircle2,
  Clock,
  PenLine,
  Layers,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { SectionCard } from "../../../../components/ui/SectionCard";
import { Input } from "../../../../components/ui/Input";
import { Button } from "../../../../components/ui/Button";
import { Badge } from "../../../../components/ui/Badge";
import { EmptyState } from "../../../../components/ui/EmptyState";
import { Modal } from "../../../../components/ui/Modal";
import { Table, THead, TBody, TR, TH, TD } from "../../../../components/ui/Table";
import { showToast } from "../../../../utils/showToast";
import {
  getContractHistory,
  getActiveContract,
  getSignatoriesStatus,
  getContractSignatures,
  uploadContractVersion,
} from "../../../../api/driverContracts";
import {
  CONTRACT_DOCUMENT_TYPES,
  documentTypeLabel,
} from "../../../../constants/contractDocumentTypes";
// Lazy: pdf.js es pesado (~1 MB). Solo se descarga al abrir el editor, no en el
// bundle principal.
const ContractFieldEditor = lazy(() => import("./ContractFieldEditor"));

const MAX_PDF_BYTES = 20 * 1024 * 1024;

const fmtDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("es-CL", {
        year: "numeric",
        month: "short",
        day: "2-digit",
      })
    : "—";

const fmtDateTime = (d) => (d ? new Date(d).toLocaleString("es-CL") : "—");

const todayInput = () => new Date().toISOString().slice(0, 10);

// ─────────────────────────────────────────────────────────────────────────────
// Sección: Contrato de prestación de servicios para repartidores.
// El operador sube versiones (PDF), ve el historial y el estado de firma de sus
// repartidores. Los repartidores firman desde beartrack-drivers-app.
// ─────────────────────────────────────────────────────────────────────────────

export default function DriverContractsSection() {
  const { t } = useTranslation();

  const [selectedType, setSelectedType] = useState(
    CONTRACT_DOCUMENT_TYPES[0].key
  );
  const [actives, setActives] = useState([]);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [signaturesOf, setSignaturesOf] = useState(null); // template para el modal
  const [editorTemplate, setEditorTemplate] = useState(null); // borrador en edición

  // Vigentes de todos los tipos, para el estado de las tarjetas del selector.
  const loadActives = useCallback(async () => {
    try {
      const res = await getActiveContract();
      setActives(Array.isArray(res?.data) ? res.data : []);
    } catch {
      setActives([]);
    }
  }, []);

  const loadHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const res = await getContractHistory(selectedType);
      setHistory(res?.data?.docs ?? []);
    } catch {
      setHistory([]);
      showToast(t("No se pudo cargar el historial de contratos"), false);
    } finally {
      setLoadingHistory(false);
    }
  }, [selectedType, t]);

  // Tras subir, recarga estado + historial y abre el editor sobre el nuevo
  // borrador para que el operador coloque los campos y lo active.
  const handleUploaded = useCallback(
    (created) => {
      loadActives();
      loadHistory();
      if (created?._id) setEditorTemplate(created);
    },
    [loadActives, loadHistory]
  );

  const handleActivated = useCallback(() => {
    loadActives();
    loadHistory();
  }, [loadActives, loadHistory]);

  useEffect(() => {
    loadActives();
  }, [loadActives]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const activeByType = useMemo(() => {
    const map = {};
    for (const a of actives) if (a?.documentType) map[a.documentType] = a;
    return map;
  }, [actives]);

  const selectedLabel = documentTypeLabel(selectedType);

  return (
    <div className="flex flex-col gap-5">
      <DocumentTypeBar
        selectedType={selectedType}
        onSelect={setSelectedType}
        activeByType={activeByType}
      />

      <UploadCard
        documentType={selectedType}
        documentLabel={t(selectedLabel)}
        onUploaded={handleUploaded}
      />

      <SectionCard
        icon={<FileText className="w-4 h-4" aria-hidden="true" />}
        title={`${t("Historial de versiones")} · ${t(selectedLabel)}`}
        subtitle={t(
          "Cada versión subida queda registrada. Solo una está vigente a la vez."
        )}
      >
        {loadingHistory ? (
          <div className="flex items-center gap-2 text-sm text-fg-muted py-6 justify-center">
            <Loader2 className="w-4 h-4 spin" /> {t("Cargando…")}
          </div>
        ) : history.length === 0 ? (
          <EmptyState
            icon={<FileText />}
            title={t("Aún no has subido este documento")}
            description={t(
              "Sube la primera versión para que tus repartidores puedan firmarla desde la app."
            )}
          />
        ) : (
          <Table>
            <THead>
              <TR>
                <TH>{t("Versión")}</TH>
                <TH>{t("Estado")}</TH>
                <TH>{t("Vigente desde")}</TH>
                <TH>{t("Subido por")}</TH>
                <TH>{t("Firmas")}</TH>
                <TH>{t("Acciones")}</TH>
              </TR>
            </THead>
            <TBody>
              {history.map((tpl) => (
                <TR key={tpl._id}>
                  <TD className="font-medium">v{tpl.version}</TD>
                  <TD>
                    {tpl.status === "active" ? (
                      <Badge tone="success" dot>
                        {t("Vigente")}
                      </Badge>
                    ) : tpl.status === "draft" ? (
                      <Badge tone="warning" dot>
                        {t("Borrador")}
                      </Badge>
                    ) : (
                      <Badge tone="neutral">{t("Archivada")}</Badge>
                    )}
                  </TD>
                  <TD>{fmtDate(tpl.effectiveFrom)}</TD>
                  <TD>{tpl.uploadedByName || "—"}</TD>
                  <TD>{tpl.signatureCount ?? 0}</TD>
                  <TD>
                    <div className="flex items-center gap-2">
                      <a
                        href={tpl.pdfUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-sm text-brand-600 hover:text-brand-700"
                      >
                        <Eye className="w-4 h-4" /> {t("Ver PDF")}
                      </a>
                      {tpl.status === "draft" ? (
                        <button
                          type="button"
                          onClick={() => setEditorTemplate(tpl)}
                          className="inline-flex items-center gap-1 text-sm text-brand-600 hover:text-brand-700 font-medium"
                        >
                          <PenLine className="w-4 h-4" />{" "}
                          {t("Colocar campos y activar")}
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setSignaturesOf(tpl)}
                          className="inline-flex items-center gap-1 text-sm text-fg-muted hover:text-fg"
                        >
                          <FileSignature className="w-4 h-4" /> {t("Ver firmas")}
                        </button>
                      )}
                    </div>
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </SectionCard>

      <SignatoriesCard
        documentType={selectedType}
        documentLabel={t(selectedLabel)}
      />

      <SignaturesModal
        template={signaturesOf}
        onClose={() => setSignaturesOf(null)}
      />

      {/* Montado solo cuando hay borrador en edición → el chunk de pdf.js se
          descarga recién al abrir el editor. */}
      {editorTemplate && (
        <Suspense fallback={null}>
          <ContractFieldEditor
            template={editorTemplate}
            onClose={() => setEditorTemplate(null)}
            onActivated={handleActivated}
          />
        </Suspense>
      )}
    </div>
  );
}

// ─── Selector de tipo de documento ────────────────────────────────────────────

function DocumentTypeBar({ selectedType, onSelect, activeByType }) {
  const { t } = useTranslation();
  return (
    <SectionCard
      icon={<Layers className="w-4 h-4" aria-hidden="true" />}
      title={t("Tipos de documento")}
      subtitle={t(
        "Cada tipo tiene su propia versión vigente. Elige uno para gestionarlo."
      )}
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {CONTRACT_DOCUMENT_TYPES.map((d) => {
          const active = activeByType[d.key];
          const selected = selectedType === d.key;
          return (
            <button
              key={d.key}
              type="button"
              onClick={() => onSelect(d.key)}
              className={[
                "text-left rounded-lg border p-3 transition-colors",
                selected
                  ? "border-brand-600 bg-brand-50 ring-1 ring-brand-600"
                  : "border-border bg-bg hover:bg-bg-muted",
              ].join(" ")}
            >
              <div className="text-sm font-medium text-fg">{t(d.label)}</div>
              <div className="mt-1.5">
                {active ? (
                  <Badge tone="success" dot>
                    {t("Vigente")} v{active.version}
                  </Badge>
                ) : (
                  <Badge tone="neutral">{t("Sin documento")}</Badge>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </SectionCard>
  );
}

// ─── Subir nueva versión ──────────────────────────────────────────────────────

function UploadCard({ documentType, documentLabel, onUploaded }) {
  const { t } = useTranslation();
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState(documentLabel);
  const [effectiveFrom, setEffectiveFrom] = useState(todayInput());
  const [notes, setNotes] = useState("");
  const [uploading, setUploading] = useState(false);

  // Cambiar de tipo es un contexto de subida distinto: resetea el formulario y
  // prefija el título con la etiqueta del tipo.
  useEffect(() => {
    setTitle(documentLabel);
    setFile(null);
    setNotes("");
    if (fileRef.current) fileRef.current.value = "";
  }, [documentType, documentLabel]);

  const onPickFile = (e) => {
    const f = e.target.files?.[0] ?? null;
    if (!f) {
      setFile(null);
      return;
    }
    if (f.type !== "application/pdf") {
      showToast(t("El archivo debe ser un PDF"), false);
      e.target.value = "";
      return;
    }
    if (f.size > MAX_PDF_BYTES) {
      showToast(t("El PDF supera el tamaño máximo (20 MB)"), false);
      e.target.value = "";
      return;
    }
    setFile(f);
  };

  const canSubmit = file && title.trim() && effectiveFrom && !uploading;

  const handleUpload = async () => {
    if (!canSubmit) return;
    setUploading(true);
    try {
      const res = await uploadContractVersion(file, {
        documentType,
        title: title.trim(),
        effectiveFrom,
        notes: notes.trim() || undefined,
      });
      showToast(
        t("Contrato subido como borrador. Coloca los campos y actívalo."),
        true
      );
      setFile(null);
      setNotes("");
      if (fileRef.current) fileRef.current.value = "";
      onUploaded?.(res?.data);
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        t("No se pudo subir el contrato");
      showToast(Array.isArray(msg) ? msg.join(", ") : msg, false);
    } finally {
      setUploading(false);
    }
  };

  return (
    <SectionCard
      icon={<Upload className="w-4 h-4" aria-hidden="true" />}
      title={`${t("Subir nueva versión")} · ${documentLabel}`}
      subtitle={t(
        "El PDF que subas queda como borrador: coloca los campos (nombre, RUT, firma…) y actívalo. Al activar pasa a ser la versión vigente."
      )}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="flex flex-col gap-1 md:col-span-2">
          <label className="text-sm font-medium text-fg">
            {t("Archivo PDF del contrato")}
          </label>
          <input
            ref={fileRef}
            type="file"
            accept="application/pdf"
            onChange={onPickFile}
            disabled={uploading}
            className="block w-full text-sm text-fg-muted file:mr-3 file:rounded-md file:border-0 file:bg-brand-50 file:px-3 file:py-2 file:text-sm file:font-medium file:text-brand-700 hover:file:bg-brand-100 disabled:opacity-50"
          />
          {file && (
            <span className="text-xs text-fg-muted">
              {file.name} · {(file.size / 1024 / 1024).toFixed(2)} MB
            </span>
          )}
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium text-fg">{t("Título")}</label>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            disabled={uploading}
            maxLength={160}
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium text-fg">
            {t("Vigente desde")}
          </label>
          <Input
            type="date"
            value={effectiveFrom}
            onChange={(e) => setEffectiveFrom(e.target.value)}
            disabled={uploading}
          />
        </div>

        <div className="flex flex-col gap-1 md:col-span-2">
          <label className="text-sm font-medium text-fg">
            {t("Notas (opcional)")}
          </label>
          <Input
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={uploading}
            maxLength={1000}
            placeholder={t("Ej.: cambios respecto a la versión anterior")}
          />
        </div>
      </div>

      <div className="flex justify-end mt-2">
        <Button
          onClick={handleUpload}
          disabled={!canSubmit}
          loading={uploading}
          iconLeft={!uploading && <Upload className="w-4 h-4" />}
        >
          {uploading ? t("Subiendo…") : t("Subir y colocar campos")}
        </Button>
      </div>
    </SectionCard>
  );
}

// ─── Estado de firmas de los repartidores ─────────────────────────────────────

function SignatoriesCard({ documentType, documentLabel }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  // Al cambiar de tipo, vuelve a la primera página.
  useEffect(() => {
    setPage(1);
  }, [documentType]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    getSignatoriesStatus(documentType, page)
      .then((res) => {
        if (alive) setData(res?.data ?? null);
      })
      .catch(() => {
        if (alive) setData(null);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [documentType, page]);

  const docs = data?.docs ?? [];
  const pendingCount = useMemo(
    () => (data?.docs ?? []).filter((d) => !d.signed).length,
    [data]
  );

  return (
    <SectionCard
      icon={<Users className="w-4 h-4" aria-hidden="true" />}
      title={`${t("Estado de firma de repartidores")} · ${documentLabel}`}
      subtitle={t(
        "Pendiente = no ha firmado la versión vigente. Cada versión nueva requiere que vuelvan a firmar."
      )}
    >
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-fg-muted py-6 justify-center">
          <Loader2 className="w-4 h-4 spin" /> {t("Cargando…")}
        </div>
      ) : docs.length === 0 ? (
        <EmptyState
          icon={<Users />}
          title={t("Sin repartidores activos")}
          description={t(
            "Cuando tengas repartidores activos aparecerá aquí su estado de firma."
          )}
        />
      ) : (
        <>
          <div className="flex items-center gap-2 mb-3 text-sm text-fg-muted">
            {data?.activeVersion != null && (
              <Badge tone="brand">
                {t("Versión vigente")}: v{data.activeVersion}
              </Badge>
            )}
            {pendingCount > 0 && (
              <Badge tone="warning" dot>
                {pendingCount} {t("en esta página sin firmar")}
              </Badge>
            )}
          </div>
          <Table>
            <THead>
              <TR>
                <TH>{t("Repartidor")}</TH>
                <TH>{t("Email")}</TH>
                <TH>{t("Estado")}</TH>
                <TH>{t("Versión firmada")}</TH>
                <TH>{t("Fecha")}</TH>
                <TH>{t("Documento")}</TH>
              </TR>
            </THead>
            <TBody>
              {docs.map((d) => (
                <TR key={String(d.driverId)}>
                  <TD className="font-medium">
                    {[d.name, d.lastName].filter(Boolean).join(" ") || "—"}
                  </TD>
                  <TD>{d.email || "—"}</TD>
                  <TD>
                    {d.signed ? (
                      <Badge tone="success" dot>
                        <CheckCircle2 className="w-3 h-3" /> {t("Firmado")}
                      </Badge>
                    ) : (
                      <Badge tone="warning" dot>
                        <Clock className="w-3 h-3" /> {t("Pendiente")}
                      </Badge>
                    )}
                  </TD>
                  <TD>{d.signedVersion != null ? `v${d.signedVersion}` : "—"}</TD>
                  <TD>{fmtDate(d.signedAt)}</TD>
                  <TD>
                    {d.signedPdfUrl ? (
                      <a
                        href={d.signedPdfUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-sm text-brand-600 hover:text-brand-700"
                      >
                        <Download className="w-4 h-4" /> {t("Descargar")}
                      </a>
                    ) : (
                      "—"
                    )}
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>

          {data?.totalPages > 1 && (
            <div className="flex items-center justify-end gap-2 mt-3">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                {t("Anterior")}
              </Button>
              <span className="text-sm text-fg-muted">
                {page} / {data.totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= data.totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                {t("Siguiente")}
              </Button>
            </div>
          )}
        </>
      )}
    </SectionCard>
  );
}

// ─── Modal: firmas de una versión ─────────────────────────────────────────────

function SignaturesModal({ template, onClose }) {
  const { t } = useTranslation();
  const [signatures, setSignatures] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!template) return;
    let alive = true;
    setLoading(true);
    getContractSignatures(template._id)
      .then((res) => {
        if (alive) setSignatures(res?.data?.docs ?? []);
      })
      .catch(() => {
        if (alive) setSignatures([]);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [template]);

  return (
    <Modal
      open={!!template}
      onClose={onClose}
      size="lg"
      title={
        template
          ? `${t("Firmas")} · ${template.title} (v${template.version})`
          : t("Firmas")
      }
      description={t("Repartidores que firmaron esta versión.")}
    >
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-fg-muted py-6 justify-center">
          <Loader2 className="w-4 h-4 spin" /> {t("Cargando…")}
        </div>
      ) : signatures.length === 0 ? (
        <EmptyState
          icon={<FileSignature />}
          title={t("Nadie ha firmado esta versión todavía")}
        />
      ) : (
        <Table>
          <THead>
            <TR>
              <TH>{t("Repartidor")}</TH>
              <TH>{t("RUT")}</TH>
              <TH>{t("Fecha de firma")}</TH>
              <TH>{t("Documento firmado")}</TH>
            </TR>
          </THead>
          <TBody>
            {signatures.map((s) => (
              <TR key={s._id}>
                <TD className="font-medium">{s.driverNameSnapshot || "—"}</TD>
                <TD>{s.driverRutSnapshot || "—"}</TD>
                <TD>{fmtDateTime(s.signedAt)}</TD>
                <TD>
                  {s.signedPdfUrl ? (
                    <a
                      href={s.signedPdfUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-sm text-brand-600 hover:text-brand-700"
                    >
                      <Download className="w-4 h-4" /> {t("Descargar")}
                    </a>
                  ) : (
                    "—"
                  )}
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      )}
    </Modal>
  );
}
